import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type VatSummaryRateRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';

async function downloadXml(orgId: string, year: number, month: number) {
  const blob = await reports.vatDeclarationXml({
    organization_id: orgId,
    period_year: year,
    period_month: month,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `J0200124_${year}_${String(month).padStart(2, '0')}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];

function RateTable({ title, rows, total }: {
  title: string;
  rows: VatSummaryRateRow[];
  total: string;
}) {
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 130 }}>Ставка</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>База без ПДВ</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Сума ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                Немає записів
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.rate_code} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{r.rate_label}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(r.base)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(r.vat)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td style={{ padding: '0.5rem' }}>Усього ПДВ</td>
            <td style={{ padding: '0.5rem' }}></td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function VatSummaryPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const now = new Date();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(2);

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const data = useQuery({
    queryKey: ['vat-summary', orgId, year, month],
    queryFn: () => reports.vatSummary({
      organization_id: orgId,
      period_year: year,
      period_month: month,
    }),
    enabled: !!orgId,
  });

  const d = data.data;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Зведений звіт з ПДВ (декларація)</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => data.refetch()}>Оновити</button>
          <button
            disabled={!orgId}
            onClick={() => navigate(
              `/reports/vat-declaration-print?organization_id=${orgId}&period_year=${year}&period_month=${month}`,
            )}
          >
            🖨 Друкована форма
          </button>
          <button
            disabled={!orgId}
            onClick={() => void downloadXml(orgId, year, month)}
            title="Чорновий XML декларації — потребує доопрацювання під актуальну XSD ДПС"
          >
            ⬇ XML декларації (чорновик)
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Рік</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())}
            min={2020}
            max={2100}
          />
        </div>
        <div className="field">
          <label>Місяць</label>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
            {MONTHS_UK.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {d && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            marginTop: '1rem',
          }}>
            <RateTable
              title="Податкові зобов'язання (постачання)"
              rows={d.obligations}
              total={d.total_obligations}
            />
            <RateTable
              title="Податковий кредит (придбання)"
              rows={d.credit}
              total={d.total_credit}
            />
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1.25rem',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1rem',
            textAlign: 'center',
          }}>
            <div>
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Зобов'язання</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{fmt(d.total_obligations)}</div>
            </div>
            <div>
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Кредит</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{fmt(d.total_credit)}</div>
            </div>
            <div>
              {Number(d.to_pay) > 0 ? (
                <>
                  <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>До сплати</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#b91c1c' }}>
                    {fmt(d.to_pay)}
                  </div>
                </>
              ) : Number(d.to_refund) > 0 ? (
                <>
                  <div style={{ color: '#15803d', fontSize: '0.85rem' }}>До відшкодування</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#15803d' }}>
                    {fmt(d.to_refund)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Підсумок</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>0,00</div>
                </>
              )}
            </div>
          </div>

          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem',
            background: '#fef3c7', border: '1px solid #fbbf24',
            borderRadius: 6, fontSize: '0.85rem', color: '#92400e',
          }}>
            <strong>Чорновий XML декларації</strong>: кнопка вище згенерує
            файл у структурі, наближеній до форми Ф0200124 ДПС.
            Перед поданням потрібно: (1) звірити з актуальною XSD-схемою
            ДПС; (2) перенести/перевірити дані у кабінеті платника
            податків ДПС; (3) накласти КЕП. Без КЕП документ не приймається.
          </div>
        </>
      )}
    </div>
  );
}
